import { ref } from 'vue';
import { defineStore } from "pinia";
import axios from 'axios';
import {
  type CreateIssueDto,
  type Filter,
  type IssueDto,
  type IssueCategoryDto,
  type IssueNoteDto,
  type IssueAttachmentDto,
  IssueService,
  OpenAPI,
} from '@/client';

export const useIssueStore = defineStore("issue", () => {
  const issues = ref([] as IssueDto[]);
  const page = ref(0);
  const pageSize = ref(20);
  const totalPages = ref(0);
  const totalCount = ref(0);
  const selected = ref({} as IssueDto);
  const filters = ref([] as Filter[]);

  async function add(issue: CreateIssueDto) {
    return await IssueService.postApiIssueCreate({ requestBody: issue })
      .then(() => Promise.resolve())
      .catch((error: any) => Promise.reject(error));
  }

  async function list() {
    return await IssueService.postApiIssueListAll({
      requestBody: { page: page.value, pageSize: pageSize.value, filters: filters.value, sort: 'createdAt', sortAcsending: false }
    })
      .then((data) => {
        issues.value = data.items!;
        totalPages.value = data.totalPages!;
        totalCount.value = data.totalCount!;
        return Promise.resolve();
      })
      .catch((error: any) => Promise.reject(error));
  }

  async function setSeen(id: string) {
    return await IssueService.postApiIssueSeen({ requestBody: { id } })
      .then(() => {
        const issue = issues.value.find(i => i.meta?.id === id);
        if (issue?.meta) issue.meta.hasBeenSeen = true;
        return Promise.resolve();
      })
      .catch((error: any) => Promise.reject(error));
  }

  async function setAccepted(id: string, reason?: string | null): Promise<IssueNoteDto[]> {
    return await IssueService.postApiIssueAccepted({ requestBody: { id, reason } })
      .then((notes) => {
        const issue = issues.value.find(i => i.meta?.id === id);
        if (issue?.meta) issue.meta.hasBeenAccepted = true;
        return notes ?? [];
      })
      .catch((error: any) => Promise.reject(error));
  }

  async function comment(id: number, comment: string) {
    return await IssueService.postApiIssueComment({ requestBody: { id, comment } })
      .then((data) => {
        const issue = issues.value.find(i => i.id === id);
        if (issue && data) issue.notes = data as any;
        return Promise.resolve();
      })
      .catch((error: any) => Promise.reject(error));
  }

  async function commentWithAttachments(
    id: number,
    commentText: string,
    attachmentFileIds: string[],
    mentionedUserIds: string[]
  ): Promise<IssueNoteDto[] | null> {
    try {
      const data = await IssueService.postApiIssueComment({
        requestBody: {
          id,
          comment: commentText,
          attachmentFileIds: attachmentFileIds as any,
          mentionedUserIds: mentionedUserIds as any
        }
      });
      const notes = data as unknown as IssueNoteDto[];
      const issue = issues.value.find(i => i.id === id);
      if (issue && notes) issue.notes = notes as any;
      return notes;
    } catch {
      return null;
    }
  }

  async function setCategory(issueId: number, categoryIds: number[], categories: IssueCategoryDto[]) {
    return await IssueService.postApiIssueSetCategory({ requestBody: { issueId, categoryIds } })
      .then(() => {
        const issue = issues.value.find(i => i.id === issueId);
        if (issue) issue.categories = categories;
        return Promise.resolve();
      })
      .catch((error: any) => Promise.reject(error));
  }

  async function sendEmail(issueId: number, contactId: number, message?: string | null): Promise<boolean> {
    return await IssueService.postApiIssueSendEmail({ requestBody: { issueId, contactId, message } })
      .then((sent) => sent ?? false)
      .catch((error: any) => Promise.reject(error));
  }

  async function uploadAttachment(issueId: number, file: File): Promise<IssueAttachmentDto | null> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.request({
        baseURL: OpenAPI.BASE,
        method: 'POST',
        url: '/api/issue/upload-attachment',
        data: formData,
        headers: { Authorization: `Bearer ${OpenAPI.TOKEN}`, IssueId: issueId.toString() }
      });
      return response.data as IssueAttachmentDto;
    } catch {
      return null;
    }
  }

  async function deleteAttachment(attachmentId: number): Promise<boolean> {
    try {
      await IssueService.postApiIssueDeleteAttachment({ requestBody: { id: attachmentId } });
      return true;
    } catch {
      return false;
    }
  }

  async function getById(id: number): Promise<IssueDto | null> {
    try {
      return await IssueService.postApiIssueGet({ requestBody: { id } });
    } catch {
      return null;
    }
  }

  async function preUploadFile(file: File): Promise<{ fileId: string; fileName: string; extension: string; size?: number | null } | null> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.request({
        baseURL: OpenAPI.BASE,
        method: 'POST',
        url: '/api/issue/pre-upload-attachment',
        data: formData,
        headers: { Authorization: `Bearer ${OpenAPI.TOKEN}` }
      });
      return response.data;
    } catch {
      return null;
    }
  }

  return {
    add,
    list,
    setAccepted,
    setSeen,
    comment,
    commentWithAttachments,
    setCategory,
    sendEmail,
    uploadAttachment,
    deleteAttachment,
    preUploadFile,
    getById,

    issues,
    page,
    pageSize,
    totalPages,
    totalCount,
    selected,
    filters
  };
});
